import React, { useMemo } from "react";
import { Typography, Skeleton, Box } from "@mui/material";
import { calculateFees } from "@/lib/helpers/feeCalculator";
import { useTimezone } from "@/app/utils/TimezoneContext";
import { getRescheduleInfo } from "@/lib/helpers/meetingCancelRescheduleHelpers";
import { RESCHEDULE_NOTICE_HOURS } from "@/lib/helpers/meetingPolicy";
import moment from "moment-timezone";

const ScheduleModalSidebar = ({
  meetingData,
  feeConfig,
  estimatedTax,
  billingPreview,
  isRescheduleMode = false,
}) => {
  const { timezone } = useTimezone();

  const fees = useMemo(() => {
    if (!feeConfig) return { seekerServiceFee: 0, seekerTotalFeeDue: 0 };
    return calculateFees(meetingData?.session?.session_rate, feeConfig);
  }, [feeConfig, meetingData]);

  const rateAdjustedBySharer = meetingData?.session?.rate_adjusted_by_sharer;
  const originalSessionRate = meetingData?.session?.original_session_rate;
  const sessionRate = meetingData?.session?.session_rate;
  const effectiveServiceFee =
    billingPreview?.serviceFee ?? fees.seekerServiceFee ?? 0;
  const effectiveTax = billingPreview?.taxAmount ?? estimatedTax ?? 0;
  const waivedTotal = billingPreview?.waivedTotalAmount || 0;
  const displayServiceFee =
    waivedTotal > 0
      ? billingPreview?.originalServiceFee ?? effectiveServiceFee
      : effectiveServiceFee;
  const displayTax =
    waivedTotal > 0
      ? billingPreview?.originalTaxAmount ?? effectiveTax
      : effectiveTax;
  const effectiveTotal =
    billingPreview?.totalAmount ?? fees.seekerTotalFeeDue + (estimatedTax || 0);

  // Get reschedule information
  const rescheduleInfo = useMemo(() => {
    if (!isRescheduleMode) return null;
    return getRescheduleInfo(meetingData);
  }, [isRescheduleMode, meetingData]);

  const getCurrentMeetingTime = () => {
    if (!meetingData?.meeting_time) return "Not scheduled";

    try {
      // Convert Firestore timestamp to Date
      const time = meetingData.meeting_time.toDate
        ? meetingData.meeting_time.toDate()
        : new Date(meetingData.meeting_time);

      // Format in user's timezone with friendly format
      const formatted = moment(time)
        .tz(timezone)
        .format("ddd, MMM D, YYYY [at] h:mm A z");

      return formatted;
    } catch (error) {
      console.error("Error formatting meeting time:", error);
      return "Invalid date";
    }
  };

  if (isRescheduleMode) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2, "& .MuiTypography-root": { fontSize: "13px" } }}>
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.6 }}
          >
            {getCurrentMeetingTime()}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Duration
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {meetingData?.session?.duration} minutes
          </Typography>
        </Box>
        <Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Rescheduling
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Free
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.75 }}
          >
            No additional charges apply.
          </Typography>
        </Box>
        {rescheduleInfo?.isFinalReschedule && (
          <Typography
            variant="body2"
            sx={{ color: "text.primary", fontWeight: 600 }}
          >
            This is your final reschedule.
          </Typography>
        )}
        <Box
          component="details"
          sx={{ fontSize: "0.875rem", color: "text.secondary" }}
        >
          <Box
            component="summary"
            sx={{
              cursor: "pointer",
              fontWeight: 600,
              color: "text.primary",
              py: 0.5,
            }}
          >
            Rescheduling policy
          </Box>
          <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.6 }}>
            {rescheduleInfo?.isFirstReschedule
              ? `After this change, you can reschedule once more, unless the new meeting time is ${RESCHEDULE_NOTICE_HOURS} hours away or less.`
              : rescheduleInfo?.isFinalReschedule
              ? "No further reschedules will be available after this change."
              : rescheduleInfo?.warningMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  const feeRow = (label, value, bold = false) => (
    <Box
      sx={{
        display: { xs: "grid", sm: "flex" },
        gridTemplateColumns: "minmax(0, 44%) minmax(0, 1fr)",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: bold ? "text.primary" : "text.secondary",
          fontWeight: bold ? 600 : 400,
          fontSize: { xs: bold ? 16 : 14, sm: 13 },
          lineHeight: 1.6,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        sx={{
          textAlign: { xs: "left", sm: "right" },
          fontWeight: bold ? 600 : 500,
          fontSize: { xs: bold ? 16 : 14, sm: 13 },
          lineHeight: 1.6,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: { xs: 0, sm: 2 } }}>
      {feeRow("Duration", `${meetingData?.session?.duration} minutes`)}
      {sessionRate === 0 ? (
        <>
          {feeRow("Meeting", "Free", true)}
          <Typography variant="body2" color="text.secondary">
            This meeting is complimentary.
          </Typography>
        </>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {feeRow(
              "Session fee",
              <>
                {rateAdjustedBySharer && originalSessionRate != null && (
                  <Box
                    component="span"
                    sx={{
                      textDecoration: "line-through",
                      color: "text.secondary",
                      mr: 0.75,
                    }}
                  >
                    ${originalSessionRate}
                  </Box>
                )}
                ${sessionRate}
              </>,
            )}
            {feeConfig && (
              <>
                {feeRow("Service fee", `$${displayServiceFee.toFixed(2)}`)}
                {feeRow(
                  "Tax on service fee",
                  estimatedTax === null ? (
                    <Skeleton variant="text" width={50} />
                  ) : (
                    `$${displayTax.toFixed(2)}`
                  ),
                )}
                {waivedTotal > 0 &&
                  feeRow("Special credit", `−$${waivedTotal.toFixed(2)}`)}
              </>
            )}
          </Box>
          {feeConfig && (
            <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
              {feeRow(
                "Total due now",
                `$${effectiveTotal.toFixed(2)} USD`,
                true,
              )}
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.75, fontSize: 13, lineHeight: 1.6 }}
              >
                Charged when your meeting is scheduled.
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ScheduleModalSidebar;
